import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { theme, Typography, Modal, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { IReportType } from 'store/ducks/Global/types';
import { reportingOp } from 'store/ducks/Reporting';
import { RootState } from 'store/configureStore';
import { getReportRiskAndControlsTypes } from 'constants/workingAreas';
import ReportingLayout from 'components/Layouts/ReportingLayout/ReportingLayout';
import ControlReport from './ControlReport/ControlReport';
import ProcessOverview from './ProcessOverview';
import RiskInventory from './RiskInventory';
import ProcessInventory from './ProcessInventory';
import GroupOrganization from './GroupOrganization';
import RisksControlsGaps from './RisksControlsGaps';

import * as Styled from './Reporting.styled';

const { useToken } = theme;
const { Text } = Typography;

const ReportingRisksAndControls: React.FC = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const reportRiskAndControlsTypes = getReportRiskAndControlsTypes(t);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<IReportType | null>(
    null
  );
  const [popoverVisible, setPopoverVisible] = useState<{
    [key: string]: boolean;
  }>({});

  const { lang, activeFilters, scheme } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    activeFilters: state.global.activeFilters,
    scheme: state.global.scheme
  }));

  const handleReportClick = (report: IReportType) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  const handlePopoverVisibleChange = (
    reportTitle: string,
    visible: boolean
  ) => {
    setPopoverVisible((prev) => ({ ...prev, [reportTitle]: visible }));
  };

  const handlePopoverClose = (reportTitle: string) => {
    setPopoverVisible((prev) => ({ ...prev, [reportTitle]: false }));
  };

  useEffect(() => {
    reportingOp.fetchReport(lang, activeFilters).then(() => {});
  }, [lang, activeFilters]);

  return (
    <ReportingLayout>
      <>
        <Styled.GlobalPopoverStyles />
        <Styled.Container token={token} scheme={scheme}>
          <Styled.SubHeaderSection token={token} scheme={scheme}>
            <p>{t('GENERATE_DOWNLOAD_REPORTS')}</p>
          </Styled.SubHeaderSection>

          <Styled.ContentRow gutter={[24, 24]}>
            <Styled.MainCol xs={24} lg={10}>
              <Styled.ReportsTitle level={4}>
                {t('GENERATE_REPORTS')}
              </Styled.ReportsTitle>

              <Styled.ReportsGrid token={token} scheme={scheme}>
                {reportRiskAndControlsTypes.map((report, index) => (
                  <Styled.ReportCol key={index}>
                    <Styled.ReportCard
                      token={token}
                      scheme={scheme}
                      onClick={() => handleReportClick(report)}
                    >
                      <Styled.CardHeader />
                      <Styled.CardTitle token={token} level={5}>
                        {report.title}
                      </Styled.CardTitle>
                      {report.detailedDescription && (
                        <Styled.CardInfoIcon>
                          <Popover
                            content={
                              <Styled.PopoverContent>
                                <Styled.PopoverCloseButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePopoverClose(report.title);
                                  }}
                                >
                                  <CloseOutlined />
                                </Styled.PopoverCloseButton>
                                <Styled.PopoverTitle>
                                  {report.detailedDescription.title}
                                </Styled.PopoverTitle>
                                <Styled.PopoverSection>
                                  {report.detailedDescription.content}
                                </Styled.PopoverSection>
                                <Styled.PopoverFieldsTitle>
                                  The dataset contains the following fields:
                                </Styled.PopoverFieldsTitle>
                                <Styled.PopoverFieldsGrid>
                                  {report.detailedDescription.fields.map(
                                    (field, fieldIndex) => (
                                      <Styled.PopoverField key={fieldIndex}>
                                        <strong>{field.name}</strong>
                                        {field.description}
                                      </Styled.PopoverField>
                                    )
                                  )}
                                </Styled.PopoverFieldsGrid>
                                <Styled.PopoverFooter>
                                  {report.detailedDescription.footer}
                                </Styled.PopoverFooter>
                              </Styled.PopoverContent>
                            }
                            title={t('REPORT_DESCRIPTION')}
                            trigger='click'
                            open={popoverVisible[report.title] || false}
                            onOpenChange={(visible) =>
                              handlePopoverVisibleChange(report.title, visible)
                            }
                            overlayStyle={{ zIndex: 1050 }}
                            overlayClassName='centered-popover'
                            overlayInnerStyle={{}}
                            getPopupContainer={() => document.body}
                          >
                            <Styled.InfoIcon
                              token={token}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Popover>
                        </Styled.CardInfoIcon>
                      )}
                    </Styled.ReportCard>
                  </Styled.ReportCol>
                ))}
              </Styled.ReportsGrid>
            </Styled.MainCol>
          </Styled.ContentRow>
          <Styled.StyledModal
            scheme={scheme}
            open={isModalOpen}
            title={selectedReport?.title}
            onCancel={handleModalClose}
            footer={null}
            width='95vw'
            style={Styled.modalStyles}
            destroyOnClose
          >
            <Styled.ReportTableWrapper scheme={scheme}>
              {selectedReport?.titleKey === 'CONTROL_REPORT' && (
                <ControlReport />
              )}
              {selectedReport?.titleKey === 'PROCESSES_AND_CRITICALITIES' && (
                <ProcessOverview />
              )}
              {selectedReport?.titleKey === 'RISK_INVENTORY' && (
                <RiskInventory />
              )}
              {selectedReport?.titleKey === 'PROCESS_INVENTORY' && (
                <ProcessInventory />
              )}
              {selectedReport?.titleKey === 'GROUP_ORGANIZATION' && (
                <GroupOrganization />
              )}
              {selectedReport?.titleKey === 'RISKS_CONTROLS_GAPS' && (
                <RisksControlsGaps />
              )}
            </Styled.ReportTableWrapper>
          </Styled.StyledModal>
        </Styled.Container>
      </>
    </ReportingLayout>
  );
};

export default ReportingRisksAndControls;
